import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
  ComplaintSubNatures,
  NatureComplaintDTO,
  RequiredStepFour,
  StepCallBack,
  SubNatureComplaint,
} from "@modules/plaintes/plaintes.model.ts";
import Spinner from "@shared/Spinner.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  loadingNature,
  natureLists,
  setStepReasonValue,
  stepReasonValue,
} from "@core/slice/ComplaintSlice.ts";
import { getAllNatureComplaint } from "@core/actions/complaintAction.ts";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import {
  DynamicOptionCategory,
  DynamicOptionPostDTO,
  BACKEND_SUPPORTED_CATEGORIES,
} from "@core/modeles/dynamicOption.model.ts";
import { createDynamicOptions, fetchStandardDynamicOptions } from "@core/actions/dynamicOptionAction.ts";
import {
  incrementDynamicOption,
  makeSelectStandardLabels,
  seedCategoryFromSource,
} from "@core/slice/dynamicOptionsCountSlice.ts";

const ComplaintMotif: React.FC<StepCallBack> = ({ onNext, onPrevious }) => {
  const [errors, setErrors] = useState({
    natureOfComplaint: "",
  });
  const dispatch = useAppDispatch();
  const stepReason = useAppSelector(stepReasonValue);
  const isLoading = useAppSelector(loadingNature);
  const listsNature = useAppSelector(natureLists);

  const selectAccusedStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.ACCUSED_INSTITUTION),
    []
  );
  const selectClusterStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.CLUSTER),
    []
  );
  const selectAccusationStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.ACCUSATION_SUBNATURE),
    []
  );
  const selectDysfuncStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.DYSFUNC_SUBNATURE),
    []
  );
  const selectClaimStandard = useMemo(
    () => makeSelectStandardLabels(DynamicOptionCategory.CLAIM_SUBNATURE),
    []
  );
  const standardAccusedLabels = useAppSelector(selectAccusedStandard);
  const standardClusterLabels = useAppSelector(selectClusterStandard);
  const standardAccusationLabels = useAppSelector(selectAccusationStandard);
  const standardDysfuncLabels = useAppSelector(selectDysfuncStandard);
  const standardClaimLabels = useAppSelector(selectClaimStandard);
  const standardDysfuncCombinedLabels = useMemo(() => {
    const seen = new Set<string>();
    return [...standardDysfuncLabels, ...standardClusterLabels].filter((label) => {
      const key = label.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [standardDysfuncLabels, standardClusterLabels]);
  const standardClaimCombinedLabels = useMemo(() => {
    const seen = new Set<string>();
    return [...standardClaimLabels, ...standardClusterLabels].filter((label) => {
      const key = label.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [standardClaimLabels, standardClusterLabels]);


  const [natureComplaintList, setNatureComplaintList] =
    useState<NatureComplaintDTO[]>();
  const [natureComplaintSelectedList, setNatureComplaintSelectedList] =
    useState<NatureComplaintDTO[]>([]);
  const [subNatureComplaintSelectedList, setSubNatureComplaintSelectedList] =
    useState<SubNatureComplaint[]>([]);

  const [errorRequired, setErrorRequired] = useState<RequiredStepFour>({
    natureDysError: "",
    institutionError: "",
    programConcernedError: "",
  });

  const [selectedReasons, setSelectedReasons] = useState<number[]>([]);
  const [activeIndex, setActiveIndex] = useState<number[]>([]);
  const [idSubSelected, setIdSubSelected] = useState<ComplaintSubNatures[]>(
    () => {
      const raw = stepReason?.stepFour.complaintSubNatures ?? [];
      const seen = new Set<number>();
      return raw.filter((s) => {
        if (seen.has(s.subnatureId)) return false;
        seen.add(s.subnatureId);
        return true;
      });
    }
  );
  const [errorOther, setErrorOther] = useState<any>({});
  // IDs des custom-subs dont la valeur vient d'un chip (suggestion) — masque la ligne "Autres"
  const [chipSelectedSubs, setChipSelectedSubs] = useState<Set<number>>(new Set());

  const programConcerned = natureComplaintList?.find(
    (a) => a.idLabel === "PROG_CONC"
  );

  useEffect(() => {
    dispatch(getAllNatureComplaint());
    // Appel GET uniquement pour les catégories supportées par le backend
    [
      DynamicOptionCategory.ACCUSED_INSTITUTION,
      DynamicOptionCategory.CLUSTER,
      DynamicOptionCategory.ACCUSATION_SUBNATURE,
      DynamicOptionCategory.DYSFUNC_SUBNATURE,
      DynamicOptionCategory.CLAIM_SUBNATURE,
    ]
      .filter((cat) => BACKEND_SUPPORTED_CATEGORIES.has(cat))
      .forEach((cat) => dispatch(fetchStandardDynamicOptions(cat)));
    dispatch(seedCategoryFromSource({
      sourceCategory: DynamicOptionCategory.ACCUSATION_SUBNATURE,
      targetCategories: [
        DynamicOptionCategory.DYSFUNC_SUBNATURE,
        DynamicOptionCategory.CLAIM_SUBNATURE,
      ],
    }));
    if (stepReason && stepReason.stepFour) {
      setSubNatureComplaintSelectedList(
        stepReason.stepFour.subNatureComplaintSelectedList
      );
      setNatureComplaintSelectedList(
        stepReason.stepFour.natureComplaintSelectedList
      );
      setSelectedReasons(
        stepReason.stepFour.subNatureComplaintSelectedList.map(
          (subNature: SubNatureComplaint) => subNature.id
        )
      );
    }
  }, [stepReason, dispatch]);

  const requiredNatureDysList = [
    "REG_PROC",
    "SERV_IMPL",
    "DISTRIBUTION_MEC",
    "DISSAT",
    "ACCUS",
    "CLAIM",
    "PROG_IMPL",
  ];
  const requiredInstitution = [
    "PROG_IMPL",
    "ACCUS",
    "CLAIM",
    "DISSAT",
    "ADVICE",
    "DIST_CASE",
  ];
  const requiredConcernedProgram = [
    "CLAIM",
    "PROG_IMPL",
    "DISSAT",
    "ADVICE",
    "DIST_CASE",
  ];
  useEffect(() => {
    const natureDys = natureComplaintSelectedList.find((val) =>
      requiredNatureDysList.includes(val.idLabel)
    );
    const institution = natureComplaintSelectedList.find((val) =>
      requiredInstitution.includes(val.idLabel)
    );
    const concernedProgram = natureComplaintSelectedList.find((val) =>
      requiredConcernedProgram.includes(val.idLabel)
    );
    const mappingRequired = {
      INSTIT_IMP: institution,
      DYSFUNC_NAT: natureDys,
      PROG_CONC: concernedProgram,
    };
    let newNatureComplaintList = listsNature.filter(
      (item: any) => item.type === "REASON"
    );
    Object.entries(mappingRequired).forEach(
      (value: [string, NatureComplaintDTO | undefined]) => {
        if (!value[1]) {
          newNatureComplaintList = newNatureComplaintList.filter(
            (item: any) => item.idLabel !== value[0]
          );
        }
      }
    );
    const removeNatureRequired = ["DYSFUNC_NAT", "INSTIT_IMP", "PROG_CONC"];
    const reorderList = [
      ...newNatureComplaintList.filter(
        (val) => !removeNatureRequired.includes(val.idLabel)
      ),
      ...newNatureComplaintList.filter((val) =>
        removeNatureRequired.includes(val.idLabel)
      ),
    ];
    setNatureComplaintList(reorderList);
    const checkRequiredProgramConcerned = natureComplaintSelectedList.filter(
      (val) => requiredConcernedProgram.includes(val.idLabel)
    );
    if (checkRequiredProgramConcerned.length === 0 && programConcerned) {
      setIdSubSelected((prev) =>
        prev.filter((val) => val.natureId !== programConcerned.id)
      );
    }
  }, [listsNature, natureComplaintSelectedList]);

  useEffect(() => {
    const activeIndices = natureComplaintList
      ?.map((nature, index) => {
        const hasSelectedSubNatures = nature.subNatures.some((subNature) =>
          selectedReasons.includes(subNature.id)
        );
        return hasSelectedSubNatures ? index : null;
      })
      .filter((index) => index !== null) as number[];

    setActiveIndex(activeIndices);
  }, [selectedReasons, natureComplaintList]);

  const handleValidate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const filterSubSelected = subNatureComplaintSelectedList?.filter(
      (val) => val.isCustom === true
    );
    idSubSelected.forEach(({ natureId, subnatureId, additionalInfo }) => {
      if (
        (!additionalInfo || additionalInfo.trim() === "") &&
        filterSubSelected.map((val) => val.id).includes(subnatureId)
      ) {
        newErrors[natureId] = "Ce champ est requis";
      }
    });

    setErrorOther(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    const requiredInstitution = ["CLAIM", "ACCUS", "PROG_IMPL"];
    const requiredConcernedProgram = ["CLAIM", "PROG_IMPL"];
    const idLabelProgramConcerned =
      natureComplaintList &&
      natureComplaintList.find((val) => val.idLabel === "PROG_CONC");
    const subNatureEntry = idSubSelected.find(
      (sub) => sub.natureId === idLabelProgramConcerned?.id
    );
    const hasNoSubNatures =
      !subNatureComplaintSelectedList ||
      subNatureComplaintSelectedList.length === 0;
    const isInfoEmpty =
      subNatureEntry === undefined ||
      subNatureEntry?.additionalInfo?.trim() === "";
    const natureDys = natureComplaintSelectedList.find((val) =>
      requiredNatureDysList.includes(val.idLabel)
    );
    const institutionNature = natureComplaintSelectedList.find((val) =>
      requiredInstitution.includes(val.idLabel)
    );

    const programConcerned = natureComplaintSelectedList.find((val) =>
      requiredConcernedProgram.includes(val.idLabel)
    );
    const natDysSelected =
      natureDys &&
      natureComplaintSelectedList
        .map((nature) => nature.idLabel)
        .includes("DYSFUNC_NAT");
    const institutionSelected =
      institutionNature &&
      natureComplaintSelectedList
        .map((nature) => nature.idLabel)
        .includes("INSTIT_IMP");
    const programConcernedSelected =
      idSubSelected.length > 0 && programConcerned;
    const isValid = handleValidate();
    if (hasNoSubNatures) {
      setErrors((prev) => ({
        ...prev,
        natureOfComplaint: "Veuillez cocher la nature de la plainte",
      }));
      return;
    }
    if (!natDysSelected || !institutionSelected || !programConcernedSelected) {
      let errors = errorRequired;
      const idLabelsRequired = [];
      if (natureDys) {
        idLabelsRequired.push("DYSFUNC_NAT");
        if (natDysSelected === false) {
          errors = {
            ...errors,
            natureDysError:
              "Veuillez renseigner la Nature de dysfonctionnement",
          };
        } else errors = { ...errors, natureDysError: "" };
      }
      if (institutionNature) {
        idLabelsRequired.push("INSTIT_IMP");
        if (institutionSelected === false) {
          errors = {
            ...errors,
            institutionError:
              "Veuillez renseigner l'Instituion ou structure mise en cause",
          };
        } else errors = { ...errors, institutionError: "" };
      }
      if (programConcerned) {
        idLabelsRequired.push("PROG_CONC");
        if (programConcernedSelected === false || isInfoEmpty) {
          errors = {
            ...errors,
            programConcernedError:
              "Veuillez indiquer le nom du programme ou activité concerné(e)",
          };
        } else errors = { ...errors, programConcernedError: "" };
      }
      if (idLabelsRequired && idLabelsRequired.length > 0) {
        const idLabels: string[] | undefined = natureComplaintList?.map(
          (nature) => nature.idLabel
        );
        const newActiveIndex = idLabelsRequired.map(
          (val) => idLabels!.findIndex((idLabel) => idLabel === val)!
        );
        setActiveIndex((prev) => [...prev, ...newActiveIndex]);
        setErrorRequired((prev) => ({
          ...prev,
          ...errors,
        }));
        if (
          Object.values(errors).filter((error) => error.trim().length > 0)
            .length > 0
        ) {
          return;
        }
      }
    }
    if (!isValid) {
      return;
    }
    setErrors((prev) => ({
      ...prev,
      natureOfComplaint: "",
    }));
    if (stepReason) {
      const updateStepFour = (() => {
        if (!isInfoEmpty) {
          const natureProgram = natureComplaintList?.find(
            (n) => n.idLabel === "PROG_CONC"
          );
          const subProgram = natureProgram?.subNatures ?? [];
          return {
            natureComplaintSelectedList: [
              ...natureComplaintSelectedList.filter(
                (nature) => nature.idLabel !== "PROG_CONC"
              ),
              ...(natureProgram ? [natureProgram] : []),
            ],
            subNatureComplaintSelectedList: [
              ...subNatureComplaintSelectedList.filter(
                (sub) => !subProgram.some((e) => e.id === sub.id)
              ),
              ...subProgram,
            ],
            complaintSubNatures: idSubSelected,
          };
        } else {
          return {
            natureComplaintSelectedList: natureComplaintSelectedList.filter(
              (nature) => nature.idLabel !== "PROG_CONC"
            ),
            subNatureComplaintSelectedList: subNatureComplaintSelectedList,
            complaintSubNatures: idSubSelected,
          };
        }
      })();

      // ── POST /dynamic-option : 1 occurrence par soumission (backend gère le comptage) ─
      {
        const rawWithAdditional2 = idSubSelected.filter((sub) => sub.additionalInfo?.trim());
        const seenKeys2 = new Set<string>();
        const dynamicPayload: DynamicOptionPostDTO[] = [];
        const allItems: { labelOriginal: string; category: DynamicOptionCategory; natureId: number }[] = [];
        for (const sub of rawWithAdditional2) {
          const parentNature = natureComplaintList?.find((n) => n.id === Number(sub.natureId));
          const category =
            parentNature?.idLabel === "INSTIT_IMP"
              ? DynamicOptionCategory.ACCUSED_INSTITUTION
              : parentNature?.idLabel === "ACCUS"
              ? DynamicOptionCategory.ACCUSATION_SUBNATURE
              : parentNature?.idLabel === "DYSFUNC_NAT"
              ? DynamicOptionCategory.DYSFUNC_SUBNATURE
              : parentNature?.idLabel === "CLAIM"
              ? DynamicOptionCategory.CLAIM_SUBNATURE
              : DynamicOptionCategory.CLUSTER;
          const label = sub.additionalInfo!.trim();
          const dedupKey = `${category}__${label.toLowerCase()}`;
          if (seenKeys2.has(dedupKey)) continue;
          seenKeys2.add(dedupKey);
          allItems.push({ labelOriginal: label, category, natureId: sub.subnatureId });
        }
        // Comptage localStorage pour TOUTES les catégories
        for (const item of allItems) {
          dispatch(incrementDynamicOption({
            category: item.category as string,
            label: item.labelOriginal,
          }));
        }
        // POST backend uniquement pour les catégories supportées par le backend
        for (const item of allItems) {
          if (BACKEND_SUPPORTED_CATEGORIES.has(item.category as DynamicOptionCategory)) {
            dynamicPayload.push(item);
          } else {
            console.warn(
              `[DynamicOptions] ⏭️ POST ignoré pour "${item.labelOriginal}" — catégorie "${item.category}" non encore supportée par le backend`
            );
          }
        }
        if (dynamicPayload.length > 0) {
          dispatch(createDynamicOptions(dynamicPayload));
        }
      }
      // ─────────────────────────────────────────────────────────────────────────────────

      dispatch(
        setStepReasonValue({
          ...stepReason,
          stepFour: updateStepFour,
        })
      );
      onNext();
    }
  };

  function handlePreviousStep() {
    if (onPrevious) {
      const subNatureEntry = idSubSelected.find(
        (sub) => sub.natureId === programConcerned?.id
      );
      const isInfoEmpty =
        !subNatureEntry ||
        subNatureEntry?.additionalInfo ||
        subNatureEntry?.additionalInfo?.trim() === "";
      setErrors((prev) => ({
        ...prev,
        natureOfComplaint: "",
      }));
      if (stepReason) {
        const updateStepFour = (() => {
          if (!isInfoEmpty) {
            const natureProgram = natureComplaintList?.find(
              (n) => n.idLabel === "PROG_CONC"
            );
            const subProgram = natureProgram?.subNatures ?? [];
            return {
              natureComplaintSelectedList: [
                ...natureComplaintSelectedList.filter(
                  (nature) => nature.idLabel !== "PROG_CONC"
                ),
                ...(natureProgram ? [natureProgram] : []),
              ],
              subNatureComplaintSelectedList: [
                ...subNatureComplaintSelectedList.filter(
                  (sub) => !subProgram.some((e) => e.id === sub.id)
                ),
                ...subProgram,
              ],
              complaintSubNatures: idSubSelected,
            };
          } else {
            return {
              natureComplaintSelectedList: natureComplaintSelectedList.filter(
                (nature) => nature.idLabel !== "PROG_CONC"
              ),
              subNatureComplaintSelectedList: subNatureComplaintSelectedList,
              complaintSubNatures: idSubSelected,
            };
          }
        })();
        dispatch(
          setStepReasonValue({
            ...stepReason,
            stepFour: updateStepFour,
          })
        );
        onPrevious();
      }
    }
  }

  const handleReasonToggle = (
    selectedSubNature: number,
    parentNature: NatureComplaintDTO
  ) => {
    setSelectedReasons((prevReasons) => {
      const isSelected = prevReasons.includes(selectedSubNature);
      // Mettez à jour les raisons sélectionnées
      let updatedReasons = isSelected
        ? prevReasons.filter((reason) => reason !== selectedSubNature)
        : [...prevReasons, selectedSubNature];
      //
      let newSubNatureComplaintSelectedList = subNatureComplaintSelectedList;
      let newNatureComplaintSelectedList = natureComplaintSelectedList;
      const mappingRequired = {
        DYSFUNC_NAT: requiredNatureDysList,
        INSTIT_IMP: requiredInstitution,
      };
      Object.entries(mappingRequired).forEach((value) => {
        // const isNatureAlreadySelected = newNatureComplaintSelectedList.filter(nature => nature.idLabel === parentNature?.idLabel).length > 0
        // Testez si le Sub-Nature est déjà sélectionné
        const isSubNatureAlreadySelected =
          newSubNatureComplaintSelectedList.filter(
            (nature) => nature.id === selectedSubNature
          ).length > 0;
        if (
          isSubNatureAlreadySelected &&
          value[1].includes(parentNature.idLabel)
        ) {
          let filteredRequiredNature = value[1];
          const currentParentNatureNeedNatureRequired =
            newSubNatureComplaintSelectedList.filter(
              (subNature) =>
                subNature.natureId === parentNature.id &&
                subNature.id !== selectedSubNature
            ).length > 0;
          if (!currentParentNatureNeedNatureRequired) {
            // Filtrez les natures qui requiert mappingRequired
            filteredRequiredNature = filteredRequiredNature.filter(
              (idLabel) => idLabel !== parentNature.idLabel
            );
          }
          // Testez si d'autres natures ont encore besoin du mappingRequired
          const needNatureRequired =
            newNatureComplaintSelectedList.filter((nature) =>
              filteredRequiredNature.includes(nature.idLabel)
            ).length > 0;
          if (!needNatureRequired) {
            const natureList = natureComplaintList?.find(
              (nature) => nature.idLabel === value[0]
            );
            newNatureComplaintSelectedList =
              newNatureComplaintSelectedList.filter(
                (nature) => nature.idLabel !== natureList?.idLabel
              );
            const idSubNatureList = natureList?.subNatures.map(
              (nature) => nature.id
            );
            newSubNatureComplaintSelectedList =
              newSubNatureComplaintSelectedList.filter(
                (subNature) => !idSubNatureList?.includes(subNature.id)
              );
            updatedReasons = updatedReasons.filter(
              (reason) => !idSubNatureList?.includes(reason)
            );
          }
        }
      });
      // Mettez à jour les sous-natures sélectionnées
      setSubNatureComplaintSelectedList(
        isSelected
          ? newSubNatureComplaintSelectedList.filter(
              (subNature) => subNature.id !== selectedSubNature
            )
          : [
              ...newSubNatureComplaintSelectedList,
              parentNature.subNatures.find(
                (sub) => sub.id === selectedSubNature
              )!,
            ]
      );
      // Mettez à jour les natures sélectionnées
      setNatureComplaintSelectedList((prevList = []) => {
        const isAnySubNatureSelected = parentNature.subNatures.some(
          (subNature) => updatedReasons.includes(subNature.id)
        );

        const alreadySelected = newNatureComplaintSelectedList.some(
          (nature) => nature.id === parentNature.id
        );

        if (isAnySubNatureSelected && !alreadySelected) {
          return [...newNatureComplaintSelectedList, parentNature];
        } else if (!isAnySubNatureSelected && alreadySelected) {
          return newNatureComplaintSelectedList.filter(
            (nature) => nature.id !== parentNature.id
          );
        }

        return newNatureComplaintSelectedList;
      });

      return updatedReasons; // Retournez la nouvelle liste des raisons
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      natureOfComplaint: "",
    }));
  };

  const NatureSelected = natureComplaintSelectedList.find(
    (nature) =>
      nature.idLabel === "PROG_IMPL" ||
      nature.idLabel === "CLAIM" ||
      nature.idLabel === "ACCUS"
  );
  const requiredSelectedProgramConcerned = natureComplaintSelectedList.find(
    (nature) => nature.idLabel === "PROG_IMPL" || nature.idLabel === "CLAIM"
  );

  return (
    <div className="column gap-[20px] h-12rem card-stepper">
      <div className="stitle">Motif de la plainte</div>
      {isLoading === "loading" ? (
        <Spinner />
      ) : (
        <>
          <Accordion
            multiple
            className=""
            activeIndex={activeIndex}
            onTabChange={(e) =>
              setActiveIndex(Array.isArray(e.index) ? e.index : [e.index])
            }
          >
            {natureComplaintList &&
              natureComplaintList?.map((nature) => (
                <AccordionTab
                  key={nature.id}
                  header={
                    nature.idLabel === "DYSFUNC_NAT" ||
                    (NatureSelected !== undefined &&
                      nature.idLabel === "INSTIT_IMP") ||
                    (nature.idLabel === "PROG_CONC" &&
                      requiredSelectedProgramConcerned) ? (
                      <p
                        className={classNames("", {
                          "transverse italic":
                            nature.idLabel === "INSTIT_IMP" ||
                            nature.idLabel === "DYSFUNC_NAT" ||
                            nature.idLabel === "PROG_CONC",
                        })}
                      >
                        {nature.name + " (Obligatoire)"}
                      </p>
                    ) : (
                      <p
                        className={classNames("", {
                          "transverse italic":
                            nature.idLabel === "INSTIT_IMP" ||
                            nature.idLabel === "DYSFUNC_NAT" ||
                            nature.idLabel === "PROG_CONC",
                        })}
                      >
                        {nature.name}
                      </p>
                    )
                  }
                >
                  <div className="p-2 space-y-2">
                    {(() => {
                      const chips =
                        nature.idLabel === "INSTIT_IMP"
                          ? standardAccusedLabels
                          : nature.idLabel === "ACCUS" && nature.subNatures.some((s) => s.isCustom)
                          ? standardAccusationLabels
                          : nature.idLabel === "DYSFUNC_NAT" && nature.subNatures.some((s) => s.isCustom)
                          ? standardDysfuncCombinedLabels
                          : nature.idLabel === "CLAIM" && nature.subNatures.some((s) => s.isCustom)
                          ? standardClaimCombinedLabels
                          : nature.subNatures.some((s) => s.isCustom) && nature.idLabel !== "PROG_CONC"
                          ? standardClusterLabels
                          : [];
                      if (chips.length === 0) return null;
                      const customSub = nature.subNatures.find((s) => s.isCustom);
                      if (!customSub) return null;
                      const isCustomChecked = selectedReasons.includes(customSub.id);
                      const customEntry = idSubSelected.find((a) => a.subnatureId === customSub.id);
                      return (
                        <div className="flex flex-col gap-y-1 mb-2">
                          {chips.map((chipLabel) => {
                            const isActive = isCustomChecked && customEntry?.additionalInfo === chipLabel;
                            const chipId = `sugg-${customSub.id}-${chipLabel}`;
                            return (
                              <div key={chipLabel} className="flex items-center">
                                <Checkbox
                                  inputId={chipId}
                                  checked={isActive}
                                  onChange={() => {
                                    setErrorOther({});
                                    setErrors({ natureOfComplaint: "" });
                                    setErrorRequired({ natureDysError: "", institutionError: "", programConcernedError: "" });
                                    if (isActive) {
                                      handleReasonToggle(customSub.id, nature);
                                      setIdSubSelected((prev) =>
                                        prev.filter((sub) => sub.subnatureId !== customSub.id)
                                      );
                                      setChipSelectedSubs((prev) => {
                                        const next = new Set(prev);
                                        next.delete(customSub.id);
                                        return next;
                                      });
                                    } else {
                                      if (!isCustomChecked) {
                                        handleReasonToggle(customSub.id, nature);
                                      }
                                      setIdSubSelected((prev) => {
                                        const exists = prev.find((a) => a.subnatureId === customSub.id);
                                        if (exists) {
                                          return prev.map((sub) =>
                                            sub.subnatureId === customSub.id
                                              ? { ...sub, additionalInfo: chipLabel }
                                              : sub
                                          );
                                        }
                                        return [...prev, { natureId: nature.id, subnatureId: customSub.id, additionalInfo: chipLabel }];
                                      });
                                      setChipSelectedSubs((prev) => new Set([...prev, customSub.id]));
                                    }
                                  }}
                                />
                                <label htmlFor={chipId} className="ml-2 cursor-pointer select-none">
                                  {chipLabel}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {[
                      ...nature.subNatures.filter(
                        (item) => item.isCustom === false
                      ),
                      ...nature.subNatures.filter(
                        (item) => item.isCustom === true
                      ),
                    ].map((item, index) => {
                      if (item.isCustom && chipSelectedSubs.has(item.id)) return null;
                      const isIdSelected = idSubSelected.find(
                        (a) => a.subnatureId === item.id
                      );
                      const idProgram = natureComplaintList.find(
                        (a) => a.idLabel === "PROG_CONC"
                      )?.subNatures[0].id!;
                      const subnatureNullEntry = idSubSelected.find(
                        (sub) =>
                          sub.natureId === programConcerned?.id &&
                          sub.subnatureId === idProgram
                      );
                      return (
                        <div
                          className={classNames("", {
                            "lg:flex w-full":
                              nature.idLabel === "INSTIT_IMP" &&
                              index < nature.subNatures.length - 1,
                          })}
                          key={item.id}
                        >
                          <div
                            key={item.id}
                            className={classNames("flex items-center w-full", {
                              "w-[25%]": nature.idLabel === "INSTIT_IMP",
                            })}
                          >
                            {nature.idLabel !== "PROG_CONC" && (
                              <Checkbox
                                inputId={`${item.id}`}
                                checked={selectedReasons.includes(item.id)}
                                disabled={
                                  item.isNonReceipt === true &&
                                  natureComplaintSelectedList.find(
                                    (nature) => nature.idLabel === "REG_PROC"
                                  ) !== undefined
                                }
                                onChange={() => {
                                  setErrorOther({});
                                  setErrors({
                                    natureOfComplaint: "",
                                  });
                                  setErrorRequired({
                                    natureDysError: "",
                                    institutionError: "",
                                    programConcernedError: "",
                                  });
                                  handleReasonToggle(item.id, nature);
                                  if (item.isCustom === true || nature.idLabel === "INSTIT_IMP") {
                                    if (isIdSelected !== undefined) {
                                      setIdSubSelected((prev) =>
                                        prev.filter(
                                          (a) => a.subnatureId !== item.id
                                        )
                                      );
                                    } else
                                      setIdSubSelected((prev) => [
                                        ...prev,
                                        {
                                          natureId: nature.id,
                                          subnatureId: item.id,
                                          additionalInfo: "",
                                        },
                                      ]);
                                  }
                                }}
                              />
                            )}
                            <label
                              htmlFor={`${item.id}`}
                              className={classNames("ml-2", {
                                "text-gray-300":
                                  item.isNonReceipt === true &&
                                  natureComplaintSelectedList.find(
                                    (nature) => nature.idLabel === "REG_PROC"
                                  ) !== undefined,
                              })}
                            >
                              {item.description}
                            </label>
                          </div>
                          {selectedReasons.includes(item.id) &&
                            isIdSelected !== undefined &&
                            nature.idLabel !== "PROG_CONC" && (
                              <div className="flex flex-col w-[75%] mt-1">
                                {item.isCustom ? (
                                  <label
                                    htmlFor={`${item.id}-nature-${item.natureId}`}
                                    className="text-sm font-medium"
                                  >
                                    Veuillez préciser :
                                  </label>
                                ) : (
                                  <label
                                    htmlFor={`${item.id}-nature-${item.natureId}`}
                                  >
                                    Nom de l'organisme concerné (si connu)
                                  </label>
                                )}
                                {/* ──────────────────────────────────────── */}
                                <InputText
                                  id={`${item.id}-nature-${item.natureId}`}
                                  placeholder="Saisir ici..."
                                  className={classNames("w-1/2 flex-1")}
                                  value={
                                    selectedReasons.includes(item.id) &&
                                    isIdSelected?.additionalInfo
                                      ? isIdSelected?.additionalInfo
                                      : ""
                                  }
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    // Saisie manuelle → on désactive le mode chip pour ce sous-type
                                    setChipSelectedSubs((prev) => {
                                      const next = new Set(prev);
                                      next.delete(isIdSelected.subnatureId);
                                      return next;
                                    });
                                    setIdSubSelected((prev) =>
                                      prev.map((sub) =>
                                        sub.subnatureId ===
                                        isIdSelected.subnatureId
                                          ? {
                                              ...sub,
                                              additionalInfo: newValue,
                                            }
                                          : sub
                                      )
                                    );
                                  }}
                                />
                              </div>
                            )}
                          {nature.idLabel === "PROG_CONC" && (
                            <InputText
                              id={`${item.id}-nature-${item.natureId}`}
                              placeholder=""
                              value={
                                subnatureNullEntry?.additionalInfo
                                  ? subnatureNullEntry?.additionalInfo
                                  : ""
                              }
                              onChange={(e) => {
                                const newValue = e.target.value;

                                setIdSubSelected((prev) => {
                                  const existing = prev.find(
                                    (sub) =>
                                      sub.natureId === programConcerned?.id &&
                                      sub.subnatureId === idProgram
                                  );
                                  if (existing) {
                                    return prev.map((sub) =>
                                      sub.natureId === programConcerned?.id &&
                                      sub.subnatureId === idProgram
                                        ? {
                                            ...sub,
                                            additionalInfo: newValue,
                                          }
                                        : sub
                                    );
                                  } else {
                                    return [
                                      ...prev,
                                      {
                                        natureId: nature.id,
                                        subnatureId: idProgram,
                                        additionalInfo: newValue,
                                      },
                                    ];
                                  }
                                });
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {errorRequired.natureDysError &&
                    nature.idLabel === "DYSFUNC_NAT" && (
                      <small className="text-red-600 block mt-2">
                        {errorRequired.natureDysError}
                      </small>
                    )}
                  {errorRequired.institutionError &&
                    nature.idLabel === "INSTIT_IMP" && (
                      <small className="text-red-600 block mt-2">
                        {errorRequired.institutionError}
                      </small>
                    )}
                  {errorRequired.programConcernedError &&
                    nature.idLabel === "PROG_CONC" && (
                      <small className="text-red-600 block mt-2">
                        {errorRequired.programConcernedError}
                      </small>
                    )}
                  {errorOther[nature.id] && (
                    <small className="text-red-600 block mt-2">
                      {errorOther[nature.id]}
                    </small>
                  )}
                </AccordionTab>
              ))}
          </Accordion>
        </>
      )}

      {errors.natureOfComplaint && (
        <small className="text-red-600">{errors.natureOfComplaint}</small>
      )}
      <div className="flex pt-4 gap-3 justify-content-end mr-6">
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Précédent"
            className="btn btn-secondary w-[120px]"
            onClick={handlePreviousStep}
          />
        </div>
        <div className="flex pt-4 justify-content-end">
          <Button
            label="Suivant"
            className="btn btn-primary w-[120px]"
            onClick={handleNextStep}
          />
        </div>
      </div>
    </div>
  );
};

export default ComplaintMotif;
